import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { fmtIT } from "@/lib/dateIT";
import { ArrowLeft, Copy, Merge, AlertTriangle } from "lucide-react";

export default function AnagraficheDoppioni() {
  const navigate = useNavigate();
  const [gruppi, setGruppi] = useState(null);
  const [scelta, setScelta] = useState({});
  const [incl, setIncl] = useState({});
  const load = () => api.get("/anagrafiche-doppioni").then(({ data }) => {
    setGruppi(data);
    setScelta(Object.fromEntries(data.map((g, i) => [i, [...g.anagrafiche].sort((a, b) => b.campi_compilati - a.campi_compilati)[0].id])));
    setIncl(Object.fromEntries(data.map((g, i) => [i, g.piva_diverse ? [] : g.anagrafiche.map((a) => a.id)])));
  });
  useEffect(() => { load(); }, []);
  const unisci = async (g, i) => {
    const principale = scelta[i];
    const ids = (incl[i] || []).filter((x) => x !== principale);
    if (!window.confirm(`Unire ${ids.length + 1} anagrafiche "${g.ragione_sociale}" in quella scelta? I dati mancanti vengono completati dalle altre e preventivi/pratiche collegati vengono spostati.`)) return;
    try {
      const { data } = await api.post("/anagrafiche-doppioni/unisci", { principale_id: principale, doppioni_ids: ids });
      toast.success(`Unite ${data.uniti} anagrafiche${data.campi_completati.length ? ` · completati: ${data.campi_completati.join(", ")}` : ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-6"><ArrowLeft className="h-4 w-4" /> Indietro</button>
        <div className="flex items-center gap-2 mb-1"><Copy className="h-6 w-6 text-red-600" /><h1 className="font-heading text-2xl font-medium text-slate-900">Anagrafiche doppie</h1></div>
        <p className="text-slate-500 text-sm mb-6">Anagrafiche con lo stesso nome. Attenzione agli omonimi tra persone fisiche: controlla codice fiscale e recapiti. Scegli quella da tenere: le altre vengono unite in essa (una copia di sicurezza resta in archivio).</p>
        {gruppi === null ? <p className="text-slate-400">Caricamento…</p> : gruppi.length === 0 ? <p className="text-slate-500" data-testid="dop-vuoto">Nessun doppione trovato.</p> : (
          <div className="space-y-4">
            {gruppi.map((g, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`dop-gruppo-${i}`}>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <p className="font-medium text-slate-900">{g.ragione_sociale} <span className="text-sm text-slate-500">· {g.n} anagrafiche</span></p>
                  <div className="flex items-center gap-3">
                    {g.piva_diverse && <span className="text-xs text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> P.IVA o codice fiscale diversi: spunta solo quelle compatibili</span>}
                    <Button size="sm" onClick={() => unisci(g, i)} disabled={(incl[i] || []).filter((x) => x !== scelta[i]).length < 1 || !(incl[i] || []).includes(scelta[i])}
                      className="bg-red-600 hover:bg-red-700 text-white" data-testid={`dop-unisci-${i}`}><Merge className="h-4 w-4 mr-1" /> Unisci le spuntate</Button>
                  </div>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-slate-500"><th className="py-1">Unisci</th><th>Tieni</th><th>P.IVA</th><th>Cod. fiscale</th><th>Comune</th><th>Email / PEC</th><th>Creata il</th><th className="text-right">Campi compilati</th></tr></thead>
                  <tbody>{g.anagrafiche.map((a) => (
                    <tr key={a.id} className="border-t border-slate-100">
                      <td className="py-1.5"><input type="checkbox" checked={(incl[i] || []).includes(a.id)} onChange={(e) => setIncl({ ...incl, [i]: e.target.checked ? [...(incl[i] || []), a.id] : (incl[i] || []).filter((x) => x !== a.id) })} data-testid={`dop-incl-${a.id}`} /></td>
                      <td><input type="radio" name={`g${i}`} checked={scelta[i] === a.id} onChange={() => setScelta({ ...scelta, [i]: a.id })} data-testid={`dop-tieni-${a.id}`} /></td>
                      <td>{a.partita_iva || "—"}</td><td>{a.codice_fiscale || "—"}</td><td>{a.comune || "—"}</td>
                      <td>{[a.email, a.pec].filter(Boolean).join(" · ") || "—"}</td><td>{fmtIT(a.created_at)}</td><td className="text-right">{a.campi_compilati}</td>
                    </tr>))}</tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
